import { useEffect, useRef, type KeyboardEvent as ReactKeyboardEvent } from 'react'
import { legacyHookAttrs } from '@/msgdeskRuntime/legacyBridge/SelectorContracts'
import { EditorFormDefaults, HiddenEditorFormFields, useEditorSection, useEditorTemplateContract } from '@/deskUiSkeletons/editor/EditorTemplateContract'
import { contract } from '@/deskUiSkeletons/editor/Editor.helpers'
import { EditorBodyFields } from '@/deskUiSkeletons/editor/EditorBodyFields'
import { EditorHeadFields, HeadShort } from '@/deskUiSkeletons/editor/components/Head/Head'
import type { EditorMode } from '@/msgdeskRuntime/contracts/editorActions'
import { SettingsInputs } from '@/deskUiSkeletons/editor/components/Settings/Settings'
import { applyMailboxMode } from '../../mailboxRoutes'
import { MailboxIcon } from '../MailboxIcons'
import { MailboxEditorActions } from './MailboxEditorActions'
import { MailboxEditorAttachments } from './MailboxEditorAttachments'

const focusableSelector = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',')

function visibleFocusableElements(root: HTMLElement | null) {
  if (!root) return []
  return Array.from(root.querySelectorAll<HTMLElement>(focusableSelector)).filter(element => (
    !element.hidden && element.getAttribute('aria-hidden') !== 'true' && element.getClientRects().length > 0
  ))
}

function trapTab(event: ReactKeyboardEvent<HTMLElement>, root: HTMLElement | null) {
  if (event.key !== 'Tab') return
  const focusable = visibleFocusableElements(root)
  if (!focusable.length) {
    event.preventDefault()
    root?.focus()
    return
  }

  const first = focusable[0]
  const last = focusable[focusable.length - 1]
  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault()
    last.focus()
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault()
    first.focus()
  }
}

export function MailboxEditorTemplate({ editorMode, active = false }: { editorMode?: EditorMode; active?: boolean }) {
  const formModel = useEditorTemplateContract(editorMode)
  const settingsSection = useEditorSection('settings')
  const settingsOpen = settingsSection.state.open ?? false
  const setSettingsOpen = (open: boolean) => settingsSection.update({ open }, 'mailbox:settings')
  const composeDialogRef = useRef<HTMLDivElement>(null)
  const settingsDialogRef = useRef<HTMLDivElement>(null)
  const previousFocusRef = useRef<HTMLElement | null>(null)

  const closeCompose = (target?: HTMLElement | null) => {
    setSettingsOpen(false)
    applyMailboxMode('msg', target || composeDialogRef.current, { updateUrl: true })
  }

  const closeSettings = () => {
    setSettingsOpen(false)
    window.requestAnimationFrame(() => {
      composeDialogRef.current?.querySelector<HTMLElement>('[data-md-mailbox-configure="true"]')?.focus()
    })
  }

  useEffect(() => {
    if (!active) return
    previousFocusRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    const frame = window.requestAnimationFrame(() => {
      const preferred = composeDialogRef.current?.querySelector<HTMLElement>('[name="to_address"], [name="name_key_smart"], input, textarea')
      ;(preferred || composeDialogRef.current)?.focus()
    })

    return () => {
      window.cancelAnimationFrame(frame)
      document.body.style.overflow = previousOverflow
      previousFocusRef.current?.focus()
    }
  }, [active])

  useEffect(() => {
    if (!settingsOpen) return
    const frame = window.requestAnimationFrame(() => {
      settingsDialogRef.current?.querySelector<HTMLElement>('[data-md-settings-close="true"]')?.focus()
    })
    return () => window.cancelAnimationFrame(frame)
  }, [settingsOpen])

  const handleComposeKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (settingsOpen) return
    if (event.key === 'Escape') {
      event.preventDefault()
      closeCompose(event.currentTarget)
      return
    }
    trapTab(event, composeDialogRef.current)
  }

  const handleSettingsKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    event.stopPropagation()
    if (event.key === 'Escape') {
      event.preventDefault()
      closeSettings()
      return
    }
    trapTab(event, settingsDialogRef.current)
  }

  return (
    <div
      className={`${active ? 'fixed' : 'hidden'} inset-0 z-[70] bg-[#17212b]/45 p-3 font-[-apple-system,BlinkMacSystemFont,'Segoe_UI',Arial,sans-serif] text-[#17212b] sm:p-8 lg:p-12`}
      data-md-mailbox-compose-modal="true"
      aria-hidden={!active}
    >
      <div
        {...legacyHookAttrs('editor.root', 'mx-auto flex h-full min-h-0 w-full max-w-[1100px] flex-col overflow-hidden rounded-md border border-[#c9d3dc] bg-white shadow-[0_22px_70px_rgba(15,23,42,0.28)]')}
        ref={composeDialogRef}
        role="dialog"
        aria-modal={active || undefined}
        aria-labelledby="md-mailbox-compose-title"
        tabIndex={-1}
        onKeyDown={handleComposeKeyDown}
      >
        <EditorFormDefaults {...formModel} editorMode={editorMode} />
        <HeadShort />
        <header className="flex h-14 flex-[0_0_56px] items-center gap-3 border-b border-[#d9e1e8] bg-white px-5 max-[640px]:px-4">
          <div className="flex h-8 w-8 flex-none items-center justify-center rounded bg-[#e8f0ff] text-[#2563eb]">
            <MailboxIcon name="mail" className="h-4 w-4" />
          </div>
          <h1 id="md-mailbox-compose-title" className="min-w-0 flex-1 truncate text-[17px] font-semibold text-[#17212b]">
            {editorMode === 'update_msg' ? 'Edit message' : 'New message'}
          </h1>
          <button
            type="button"
            className="flex h-9 w-9 flex-none items-center justify-center rounded text-[#526272] hover:bg-[#edf2f6]"
            onClick={event => closeCompose(event.currentTarget)}
            title="Close compose"
          >
            <MailboxIcon name="close" className="h-5 w-5" />
          </button>
        </header>

        <MailboxEditorActions
          editorMode={editorMode}
          settingsOpen={settingsOpen}
          onToggleSettings={() => setSettingsOpen(true)}
          onDiscard={target => closeCompose(target)}
        />

        <div className="flex min-h-0 flex-1 overflow-hidden max-[700px]:overflow-y-auto">
          <form {...contract('editor.form')} className="flex min-h-0 min-w-0 flex-1 flex-col bg-white max-[700px]:min-h-[620px]">
            <EditorHeadFields editorMode={editorMode} />
            <MailboxEditorAttachments editorMode={editorMode} />
            <EditorBodyFields />
        <HiddenEditorFormFields inputs={formModel.inputs} />
          </form>
          <div {...contract('editor.settings')} className="!hidden" hidden aria-hidden="true" />
        </div>

        <div
          className={`${settingsOpen ? 'fixed flex' : 'hidden'} inset-0 z-[90] bg-[#17212b]/50 p-4 sm:p-7 lg:p-10`}
          data-md-mailbox-settings-overlay="true"
          aria-hidden={!settingsOpen}
        >
            <div
              ref={settingsDialogRef}
              role="dialog"
              aria-modal="true"
              aria-labelledby="md-mailbox-settings-title"
              tabIndex={-1}
              className="mx-auto flex h-full min-h-0 w-full max-w-[1500px] flex-col overflow-hidden rounded-md border border-[#c9d3dc] bg-white shadow-[0_28px_80px_rgba(15,23,42,0.34)]"
              data-md-mailbox-settings="true"
              onKeyDown={handleSettingsKeyDown}
            >
              <header className="flex h-16 flex-[0_0_64px] items-center gap-3 border-b border-[#d9e1e8] px-6 max-[640px]:px-4">
                <div className="flex h-9 w-9 flex-none items-center justify-center rounded bg-[#e8f0ff] text-[#2563eb]">
                  <MailboxIcon name="settings" className="h-[18px] w-[18px]" />
                </div>
                <h2 id="md-mailbox-settings-title" className="min-w-0 flex-1 truncate text-[18px] font-semibold text-[#17212b]">Message settings</h2>
                <button
                  type="button"
                  className="h-9 rounded bg-[#2563eb] px-4 text-[12px] font-semibold text-white hover:bg-[#1d4ed8]"
                  onClick={closeSettings}
                >
                  Done
                </button>
                <button
                  type="button"
                  data-md-settings-close="true"
                  className="flex h-9 w-9 flex-none items-center justify-center rounded text-[#526272] hover:bg-[#edf2f6]"
                  onClick={closeSettings}
                  title="Close settings"
                >
                  <MailboxIcon name="close" className="h-5 w-5" />
                </button>
              </header>
              <SettingsInputs />
            </div>
          </div>
        </div>
    </div>
  )
}
